/**
 * Backend `ErrorCodes` this app branches on, rather than merely displays.
 *
 * `localizeApiError` already turns any code into `ERRORS.<CODE>` without knowing it, so a code
 * only earns a constant here when the UI has to *do* something with it — reload because the form
 * locked underneath the venue, or mark the offending field instead of showing a toast.
 */
export const ApiErrorCodes = {
  /**
   * SmartPlayr closed the management app to this venue while a session was open. Every admin call
   * now answers this; the interceptor turns the first one into the closed screen.
   */
  ManagementAccessDisabled: 'MANAGEMENT_ACCESS_DISABLED',
  /** Offered a subscription it has not taken and with no Stripe: the venue cannot go live. */
  SubscriptionRequired: 'SUBSCRIPTION_REQUIRED',
  /** The schema was rejected: `fieldErrors { key: code }` says which field and why. */
  EventFormInvalid: 'EVENT_FORM_INVALID',
  /**
   * A change the lock forbids — the first registration landed while this tab was open.
   * Also carries `fieldErrors`, so the field that may no longer change is marked in place.
   */
  EventFormLocked: 'EVENT_FORM_LOCKED',
  EventNotFound: 'EVENT_NOT_FOUND',
  EventRegistrationNotFound: 'EVENT_REGISTRATION_NOT_FOUND',
  /**
   * "Mark as paid" reached a card place. The row this tab showed as paid at the venue is stale,
   * so the registrations list is read again rather than left claiming otherwise.
   */
  EventRegistrationNotPaidAtVenue: 'EVENT_REGISTRATION_NOT_PAID_AT_VENUE',
  /**
   * "Mark as refunded" reached a desk place with no collection behind it — another tab undid the
   * payment. The row that offered the refund is stale.
   */
  EventDeskPaymentNotCollected: 'EVENT_DESK_PAYMENT_NOT_COLLECTED',
  /**
   * "Undo payment" reached a desk place whose money was already handed back: the refund has to be
   * undone first, so it never points at a collection that no longer exists. The row that offered
   * the undo did not know about the refund, so it is stale.
   */
  EventDeskPaymentRefunded: 'EVENT_DESK_PAYMENT_REFUNDED',
  /** The EVENTS flag went off under an open tab: the surface no longer exists for this venue. */
  EventsNotEnabled: 'EVENTS_NOT_ENABLED',
  EventInvalidStatusTransition: 'EVENT_INVALID_STATUS_TRANSITION',
  EventTemplateNotFound: 'EVENT_TEMPLATE_NOT_FOUND',
  /**
   * The venue already has a connected account, so there is nothing to create. Not a failure to
   * report: it means the status this card was working from was behind, and the answer is to read
   * it again rather than to leave an owner pressing a button that can never succeed.
   */
  StripeAccountAlreadyExists: 'STRIPE_ACCOUNT_ALREADY_EXISTS',
  /**
   * Card payments cannot be paused here: the venue has no running trial and no paying
   * subscription, so the desk is not offered and the card is the only way it can be booked.
   */
  OnlinePaymentsRequired: 'ONLINE_PAYMENTS_REQUIRED',
  /**
   * No connected account exists yet, so there is no account session to embed. The venue has to
   * create one first — the embedded onboarding component cannot create the account it would
   * onboard.
   */
  StripeConnectNotConnected: 'STRIPE_CONNECT_NOT_CONNECTED',
  /** The session asked for a component this platform is not enabled for. A wiring bug, not a venue one. */
  StripeConnectUnsupportedComponent: 'STRIPE_CONNECT_UNSUPPORTED_COMPONENT',
  /**
   * A court save named an attribute value the catalogue no longer has — this tab's cached
   * catalogue is stale (super-admin deleted the value). The form drops the cache and reloads it.
   */
  CourtAttributeValueNotFound: 'COURT_ATTRIBUTE_VALUE_NOT_FOUND',
  /**
   * Checkout asked for a billing cycle the venue is not offered — the yearly offer was withdrawn
   * (the agreed price lost its annual discount) while this page was open. The access is read
   * again so the cycle picker collapses to the monthly price the venue can actually buy.
   */
  BillingCycleNotOffered: 'BILLING_CYCLE_NOT_OFFERED',
} as const;

/** Error codes whose body carries per-field detail the form builder can mark inline. */
export const FIELD_ERROR_CODES: ReadonlySet<string> = new Set<string>([
  ApiErrorCodes.EventFormInvalid,
  ApiErrorCodes.EventFormLocked,
]);

/**
 * Answers to a registration row action that mean the row on screen is out of date — the place
 * is gone, left, or is not the kind of place the row said it was. The list is read again so the
 * venue is not left looking at the stale state that invited the click.
 */
export const STALE_REGISTRATION_ERROR_CODES: ReadonlySet<string> = new Set<string>([
  ApiErrorCodes.EventRegistrationNotFound,
  ApiErrorCodes.EventRegistrationNotPaidAtVenue,
  ApiErrorCodes.EventInvalidStatusTransition,
  ApiErrorCodes.EventDeskPaymentNotCollected,
  ApiErrorCodes.EventDeskPaymentRefunded,
]);
